/**
 * image_processor.js — Receipt Image Preprocessor v2
 *
 * Browser-compatible, zero external dependencies (except pdfjs-dist for PDF conversion).
 *
 * Detection strategy: sample image border pixels to estimate background
 * brightness, threshold to isolate the receipt blob, then find the four
 * diagonal-extreme corners of that blob.  Much more reliable than Hough for
 * real receipt photos because internal text/divider lines cannot interfere.
 *
 * Falls back to white-border crop when the background is too light to
 * distinguish from the receipt (e.g. white table).
 */

// ─── PDF → JPEG conversion ────────────────────────────────────────────────────

// Lazy singleton — pdfjs-dist is only loaded when the user actually uploads a PDF.
let _pdfjs = null;
async function getPdfjs() {
  if (!_pdfjs) {
    _pdfjs = await import('pdfjs-dist');
    // new URL() is resolved by webpack at build time even inside an async function
    _pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url,
    ).href;
  }
  return _pdfjs;
}

const PAGE_GAP = 8; // px of white space between pages in the combined image

/**
 * Render all pages of a PDF data URL into a single vertically-stacked JPEG.
 * Scale 2.0 gives ~150 dpi equivalent — sufficient for Gemini OCR.
 */
export async function convertPdfToJpeg(dataUrl) {
  const pdfjsLib = await getPdfjs();

  const base64 = dataUrl.split(',')[1];
  const binary = atob(base64);
  const bytes  = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

  const pdf       = await pdfjsLib.getDocument({ data: bytes }).promise;
  const numPages  = pdf.numPages;
  const scale     = 2.0;

  // Render each page to its own canvas
  const pageCanvases = [];
  for (let i = 1; i <= numPages; i++) {
    const page     = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas   = createCanvas(viewport.width, viewport.height);
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    pageCanvases.push(canvas);
  }

  // Stitch all page canvases into one tall canvas
  const totalWidth  = Math.max(...pageCanvases.map(c => c.width));
  const totalHeight = pageCanvases.reduce((sum, c) => sum + c.height, 0)
                    + PAGE_GAP * (pageCanvases.length - 1);

  const combined = createCanvas(totalWidth, totalHeight);
  const ctx      = combined.getContext('2d');
  ctx.fillStyle  = '#ffffff';
  ctx.fillRect(0, 0, totalWidth, totalHeight);

  let y = 0;
  for (const c of pageCanvases) {
    ctx.drawImage(c, 0, y);
    y += c.height + PAGE_GAP;
  }

  return combined.toDataURL('image/jpeg', 0.95);
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_OPTIONS = {
  grayscale: true,
  contrastFactor: 1.6,
  brightnessDelta: 10,
  sharpen: true,
  denoise: true,
  deskew: true,
  autoCrop: true,
  outputFormat: "image/jpeg",
  outputQuality: 0.82,
  maxWidth: 800,
  maxHeight: 1200,

  // ── Perspective correction controls ──────────────────────────────────────
  // Set false to disable entirely and always use deskew + autoCrop instead.
  perspectiveCorrection: false,

  // Background brightness (0–255) below which detection is attempted.
  // Higher → tries to correct even on light-coloured surfaces (less reliable).
  // Lower  → only corrects when background is clearly darker than the receipt.
  perspectiveBgThreshold: 205,

  // Receipt blob must cover at least this fraction of the image (0–1).
  // Raise to reject small, possibly-wrong detections.
  perspectiveMinCoverage: 0.15,

  // Maximum allowed ratio between opposite sides of the detected quad.
  // 0.3 means one side may be at most 30 % longer than the opposite side.
  // Lower → rejects more extreme perspective angles.
  perspectiveMaxDistortion: 0.3,

  // Padding added around the detected quad corners, as a fraction of the
  // shorter image dimension.  0.02 = 2 % margin so the crop is not razor-tight.
  perspectiveMargin: 0.02,

  // ── Auto-rotation ─────────────────────────────────────────────────────────
  // Detect and correct 90° / 270° rotations so the receipt is always portrait.
  // Uses row-vs-column projection variance to detect landscape orientation, then
  // picks CW vs CCW by comparing which half of the image has more distinctive
  // column content (the receipt header side should face upward).
  autoRotate: false,
};

// ─── Main Entry Point ─────────────────────────────────────────────────────────

/**
 * Preprocess a receipt image from a File, Blob, HTMLImageElement, or data URL.
 *
 * @param {File|Blob|HTMLImageElement|string} source
 * @param {Partial<typeof DEFAULT_OPTIONS>} options
 * @returns {Promise<{ dataURL: string, canvas: HTMLCanvasElement, stats: object }>}
 */
export async function preprocessReceipt(source, options = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // 1. Load with EXIF orientation applied where the runtime supports it
  const bitmap = await loadSource(source);

  // 2. Draw to working canvas at full resolution
  let canvas = createCanvas(bitmap.width, bitmap.height);
  let ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close?.();

 if (opts.autoCrop) {
    canvas = autoCropCanvas(canvas);
    console.log("auto-cropped");
  }

  if (opts.deskew)  {
     canvas = deskewCanvas(canvas);
     console.log("deskewed");
  }
 

  // 3. Attempt perspective correction — detect and warp the receipt quad
  const perspectiveCanvas = opts.perspectiveCorrection
    ? tryPerspectiveCorrection(canvas, opts)
    : null;
  const corrected = !!perspectiveCanvas;
  if (corrected) {
    canvas = perspectiveCanvas;
    ctx = canvas.getContext("2d");
  }

  // 4. Downscale to output limits (after warp so the warp works at full res)
  canvas = resizeIfNeeded(canvas, opts.maxWidth, opts.maxHeight);
  ctx = canvas.getContext("2d");

  // 4b. Auto-rotate to portrait orientation if needed
  if (opts.autoRotate) {
    const degrees = detectBestRotation(canvas);
    if (degrees !== 0) {
      canvas = rotateCanvas(canvas, degrees);
      ctx = canvas.getContext("2d");
    }
  }

  // 5. Pixel-level enhancement
  let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  if (opts.grayscale) imageData = applyGrayscale(imageData);
  if (opts.denoise)   imageData = applyGaussianBlur(imageData, canvas.width, canvas.height);
  imageData = applyContrastBrightness(imageData, opts.contrastFactor, opts.brightnessDelta);
  if (opts.sharpen)   imageData = applySharpen(imageData, canvas.width, canvas.height);
  ctx.putImageData(imageData, 0, 0);

  // 6. Fallback pipeline when perspective correction did not fire
  // if (!corrected) {
  //   // if (opts.deskew)   canvas = deskewCanvas(canvas);
  //   if (opts.autoCrop) canvas = autoCropCanvas(canvas);
  // }

  const dataURL = canvas.toDataURL(opts.outputFormat, opts.outputQuality);
  return {
    dataURL,
    canvas,
    stats: {
      width: canvas.width,
      height: canvas.height,
      format: opts.outputFormat,
      perspectiveCorrected: corrected,
    },
  };
}

// ─── Source Loading ───────────────────────────────────────────────────────────

async function loadSource(source) {
  const withOrientation = async (blob) => {
    try {
      return await createImageBitmap(blob, { imageOrientation: "from-image" });
    } catch {
      return createImageBitmap(blob);
    }
  };

  if (typeof source === "string") {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => withOrientation(img).then(resolve).catch(reject);
      img.onerror = () =>
        reject(new Error(`Failed to load image from URL: ${source.slice(0, 60)}`));
      img.src = source;
    });
  }
  if (source instanceof HTMLImageElement) return withOrientation(source);
  if (source instanceof File || source instanceof Blob) return withOrientation(source);
  throw new TypeError(
    "Unsupported source type. Pass a File, Blob, HTMLImageElement, or data URL string."
  );
}

// ─── Canvas Helpers ───────────────────────────────────────────────────────────

function createCanvas(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function resizeIfNeeded(canvas, maxW, maxH) {
  const { width, height } = canvas;
  if (width <= maxW && height <= maxH) return canvas;
  const scale = Math.min(maxW / width, maxH / height);
  const newW = Math.round(width * scale);
  const newH = Math.round(height * scale);
  const out = createCanvas(newW, newH);
  out.getContext("2d").drawImage(canvas, 0, 0, newW, newH);
  return out;
}

// ─── Perspective Correction ───────────────────────────────────────────────────

/**
 * Detect the receipt as a quadrilateral using a threshold-based approach:
 *  1. Sample border pixels to estimate background brightness.
 *  2. If background is too light (indistinguishable from receipt paper) → give up.
 *  3. Threshold the image to create a receipt-vs-background mask.
 *  4. Find the four diagonal-extreme corners of the receipt blob.
 *  5. Warp those corners to a flat rectangle.
 *
 * Returns null if detection confidence is too low.
 */
function tryPerspectiveCorrection(canvas, opts) {
  const ANALYSIS_MAX = 800;
  const scale = Math.min(1, ANALYSIS_MAX / Math.max(canvas.width, canvas.height));
  const aw = Math.round(canvas.width * scale);
  const ah = Math.round(canvas.height * scale);

  const small = createCanvas(aw, ah);
  small.getContext("2d").drawImage(canvas, 0, 0, aw, ah);
  const { data } = small.getContext("2d").getImageData(0, 0, aw, ah, {
    settings: { willReadFrequently: true },
  });

  // 1. Estimate background brightness from border pixels
  const bgBrightness = estimateBorderBrightness(data, aw, ah);

  // 2. If background is too light, fall back to autoCrop
  if (bgBrightness > opts.perspectiveBgThreshold) return null;

  // 3. Threshold: receipt pixels are significantly brighter than background
  const threshold = bgBrightness + Math.max(40, (255 - bgBrightness) * 0.45);
  let mask = new Uint8Array(aw * ah);
  for (let i = 0; i < aw * ah; i++) {
    const lum = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
    mask[i] = lum > threshold ? 1 : 0;
  }

  mask = erodeDilateMask(mask, aw, ah);

  // 4. Find the four diagonal-extreme corners of the receipt blob
  const quad = findQuadFromMask(mask, aw, ah, opts.perspectiveMinCoverage);
  if (!quad) return null;

  // 5. Expand corners outward by perspectiveMargin so the crop isn't razor-tight
  if (opts.perspectiveMargin > 0) {
    const marginPx = Math.round(Math.min(aw, ah) * opts.perspectiveMargin);
    const cx = quad.reduce((s, p) => s + p.x, 0) / 4;
    const cy = quad.reduce((s, p) => s + p.y, 0) / 4;
    for (const pt of quad) {
      const dx = pt.x - cx, dy = pt.y - cy;
      const len = Math.hypot(dx, dy) || 1;
      pt.x = clamp(Math.round(pt.x + (dx / len) * marginPx), 0, aw - 1);
      pt.y = clamp(Math.round(pt.y + (dy / len) * marginPx), 0, ah - 1);
    }
  }

  // 6. Scale corners to full-resolution coordinates
  const fullQuad = quad.map((pt) => ({ x: pt.x / scale, y: pt.y / scale }));
  return perspectiveWarp(canvas, fullQuad, opts.perspectiveMaxDistortion);
}

/**
 * Average brightness of pixels along all four image edges.
 * Uses every 5th pixel for speed.
 */
function estimateBorderBrightness(data, width, height) {
  let sum = 0, count = 0;
  const step = 5;

  for (let x = 0; x < width; x += step) {
    for (const y of [0, height - 1]) {
      const i = (y * width + x) * 4;
      sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      count++;
    }
  }
  for (let y = 0; y < height; y += step) {
    for (const x of [0, width - 1]) {
      const i = (y * width + x) * 4;
      sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      count++;
    }
  }

  return count > 0 ? sum / count : 128;
}

/**
 * Simple morphological close (dilate then erode) on a binary mask to fill
 * small holes and smooth the receipt blob boundary.
 */
function erodeDilateMask(mask, width, height) {
  const out = new Uint8Array(mask.length);
  // Dilate with 3x3 kernel
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let v = 0;
      for (let dy = -1; dy <= 1 && !v; dy++) {
        for (let dx = -1; dx <= 1 && !v; dx++) {
          const nx = x + dx, ny = y + dy;
          if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
            v = mask[ny * width + nx];
          }
        }
      }
      out[y * width + x] = v;
    }
  }
  return out;
}

/**
 * Find the receipt quadrilateral from a binary mask by locating the four
 * pixels that are most extreme along the diagonal axes:
 *   top-left     = min(x + y)
 *   top-right    = max(x - y)
 *   bottom-right = max(x + y)
 *   bottom-left  = min(x - y)
 *
 * Returns [tl, tr, br, bl] or null if the mask is too small / degenerate.
 */
function findQuadFromMask(mask, width, height, minCoverage = 0.15) {
  let minSum = Infinity,  maxSum = -Infinity;
  let minDiff = Infinity, maxDiff = -Infinity;
  let ptTL = null, ptBR = null, ptTR = null, ptBL = null;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!mask[y * width + x]) continue;
      const s = x + y, d = x - y;
      if (s < minSum)  { minSum  = s; ptTL = { x, y }; }
      if (s > maxSum)  { maxSum  = s; ptBR = { x, y }; }
      if (d < minDiff) { minDiff = d; ptBL = { x, y }; }
      if (d > maxDiff) { maxDiff = d; ptTR = { x, y }; }
    }
  }

  if (!ptTL || !ptBR || !ptTR || !ptBL) return null;

  const quad = [ptTL, ptTR, ptBR, ptBL];

  // Receipt blob must cover at least minCoverage of the analysis image
  if (quadArea(quad) < width * height * minCoverage) return null;

  // Reject degenerate quads where corners are too close together
  const spanW = Math.hypot(ptTR.x - ptTL.x, ptTR.y - ptTL.y);
  const spanH = Math.hypot(ptBL.x - ptTL.x, ptBL.y - ptTL.y);
  if (spanW < width * 0.1 || spanH < height * 0.1) return null;

  return quad;
}

/** Shoelace formula — polygon area. */
function quadArea(pts) {
  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    area += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
  }
  return Math.abs(area) / 2;
}

// ─── Auto-Rotation ────────────────────────────────────────────────────────────

/**
 * Analyse canvas content and return the clockwise rotation in degrees (0, 90,
 * or 270) needed to bring the receipt into portrait orientation.
 *
 * Algorithm:
 *  1. Build ink-weighted row sums and column sums on a small greyscale copy.
 *  2. If row variance ≥ column variance the image is already portrait → 0°.
 *  3. Otherwise the image is landscape.  Determine CW vs CCW by comparing the
 *     column-sum variance of the left and right halves: the more "distinctive"
 *     half is assumed to be the receipt header and should end up at the top.
 *     CW (90°) puts the LEFT half at the top; CCW (270°) puts the RIGHT half
 *     at the top.
 */
function detectBestRotation(canvas) {
  const MAX = 400;
  const scale = Math.min(1, MAX / Math.max(canvas.width, canvas.height));
  const w = Math.round(canvas.width  * scale);
  const h = Math.round(canvas.height * scale);

  const small = createCanvas(w, h);
  small.getContext("2d").drawImage(canvas, 0, 0, w, h);
  const { data } = small.getContext("2d").getImageData(0, 0, w, h);

  const rowSums = new Float64Array(h);
  const colSums = new Float64Array(w);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const ink = 255 - (0.299 * data[(y * w + x) * 4]
                       + 0.587 * data[(y * w + x) * 4 + 1]
                       + 0.114 * data[(y * w + x) * 4 + 2]);
      rowSums[y] += ink;
      colSums[x] += ink;
    }
  }

  const variance = (arr) => {
    const mean = arr.reduce((s, v) => s + v, 0) / arr.length;
    return arr.reduce((s, v) => s + (v - mean) ** 2, 0) / arr.length;
  };

  // Already portrait (or square) — no rotation needed
  if (variance(rowSums) >= variance(colSums)) return 0;

  // Landscape → need 90°.  Pick direction by comparing which half is the header.
  const mid      = Math.floor(w / 2);
  const leftVar  = variance(Array.from(colSums.slice(0, mid)));
  const rightVar = variance(Array.from(colSums.slice(mid)));

  // CW (90°)  → left column of original becomes top row
  // CCW (270°) → right column of original becomes top row
  return leftVar >= rightVar ? 90 : 270;
}

/**
 * Rotate a canvas clockwise by 90° or 270° (pass degrees as 90 or 270).
 */
function rotateCanvas(canvas, degrees) {
  const outW = canvas.height;
  const outH = canvas.width;
  const out  = createCanvas(outW, outH);
  const ctx  = out.getContext("2d");
  ctx.translate(outW / 2, outH / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
  return out;
}

// ─── Perspective Warp ─────────────────────────────────────────────────────────

/**
 * Warp srcCanvas so that the four corners in `quad` map to the corners of a
 * new rectangle whose dimensions match the quad's natural width × height.
 *
 * Returns null if the output would be larger than the source (bad detection)
 * or if the quad is geometrically degenerate.
 *
 * quad = [top-left, top-right, bottom-right, bottom-left]
 */
function perspectiveWarp(srcCanvas, quad, maxDistortion = 0.4) {
  const [tl, tr, br, bl] = quad;

  const topW    = Math.hypot(tr.x - tl.x, tr.y - tl.y);
  const botW    = Math.hypot(br.x - bl.x, br.y - bl.y);
  const leftH   = Math.hypot(bl.x - tl.x, bl.y - tl.y);
  const rightH  = Math.hypot(br.x - tr.x, br.y - tr.y);

  const outW = Math.max(1, Math.round((topW + botW) / 2));
  const outH = Math.max(1, Math.round((leftH + rightH) / 2));

  if (outW < 40 || outH < 40) return null;

  // Reject quads where opposite sides differ too much — indicates a bad
  // detection or an angle too extreme to warp cleanly.
  const wRatio = Math.max(topW, botW) / (Math.min(topW, botW) || 1) - 1;
  const hRatio = Math.max(leftH, rightH) / (Math.min(leftH, rightH) || 1) - 1;
  if (wRatio > maxDistortion || hRatio > maxDistortion) {
    console.warn("perspectiveWarp: distortion too large — skipping warp", { wRatio, hRatio });
    return null;
  }

  // Guard: never produce an output larger than the source.
  const srcPixels = srcCanvas.width * srcCanvas.height;
  if (outW * outH > srcPixels * 1.1) {
    console.warn("perspectiveWarp: output larger than source — skipping warp");
    return null;
  }

  // Destination rectangle corners (output pixel space)
  const dst = [
    { x: 0,    y: 0    },
    { x: outW, y: 0    },
    { x: outW, y: outH },
    { x: 0,    y: outH },
  ];

  // Homography maps output → input (inverse mapping avoids holes)
  let H;
  try {
    H = computeHomography(dst, quad);
  } catch {
    return null;
  }

  const srcCtx  = srcCanvas.getContext("2d");
  const srcData = srcCtx.getImageData(0, 0, srcCanvas.width, srcCanvas.height).data;
  const srcW    = srcCanvas.width;
  const srcH    = srcCanvas.height;

  const out    = createCanvas(outW, outH);
  const outCtx = out.getContext("2d");
  const outImg = outCtx.createImageData(outW, outH);
  const od     = outImg.data;

  for (let v = 0; v < outH; v++) {
    for (let u = 0; u < outW; u++) {
      const denom = H[6] * u + H[7] * v + H[8];
      if (Math.abs(denom) < 1e-9) continue;
      const srcX = (H[0] * u + H[1] * v + H[2]) / denom;
      const srcY = (H[3] * u + H[4] * v + H[5]) / denom;

      const [r, g, b, a] = bilinearSample(srcData, srcW, srcH, srcX, srcY);
      const oi = (v * outW + u) * 4;
      od[oi]     = r;
      od[oi + 1] = g;
      od[oi + 2] = b;
      od[oi + 3] = a;
    }
  }

  outCtx.putImageData(outImg, 0, 0);
  return out;
}

/**
 * Compute the 3×3 homography matrix H mapping src[i] → dst[i].
 * Uses DLT with h[8]=1 normalisation, solved via Gaussian elimination.
 * Returns H as a flat 9-element array [h0…h8].
 */
function computeHomography(src, dst) {
  const A = [], b = [];
  for (let i = 0; i < 4; i++) {
    const { x: X, y: Y } = src[i];
    const { x: u, y: v } = dst[i];
    A.push([X, Y, 1, 0, 0, 0, -u * X, -u * Y]);  b.push(u);
    A.push([0, 0, 0, X, Y, 1, -v * X, -v * Y]);  b.push(v);
  }
  const h = gaussianElimination(A, b);
  return [...h, 1];
}

/** Gaussian elimination with partial pivoting.  Solves Ax = b; returns x. */
function gaussianElimination(A, b) {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col++) {
    let maxRow = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(M[row][col]) > Math.abs(M[maxRow][col])) maxRow = row;
    }
    [M[col], M[maxRow]] = [M[maxRow], M[col]];
    if (Math.abs(M[col][col]) < 1e-12) throw new Error("Singular homography system");
    for (let row = 0; row < n; row++) {
      if (row === col) continue;
      const f = M[row][col] / M[col][col];
      for (let k = col; k <= n; k++) M[row][k] -= f * M[col][k];
    }
  }
  return M.map((row, i) => row[n] / row[i]);
}

/** Bilinear sample from an RGBA Uint8ClampedArray at floating-point (x, y). */
function bilinearSample(data, width, height, x, y) {
  const x0 = Math.floor(x), y0 = Math.floor(y);
  const fx = x - x0, fy = y - y0;
  const px = (cx, cy) => {
    const bx = Math.max(0, Math.min(width - 1, cx));
    const by = Math.max(0, Math.min(height - 1, cy));
    const i  = (by * width + bx) * 4;
    return [data[i], data[i + 1], data[i + 2], data[i + 3]];
  };
  const [r00, g00, b00, a00] = px(x0,     y0);
  const [r10, g10, b10, a10] = px(x0 + 1, y0);
  const [r01, g01, b01, a01] = px(x0,     y0 + 1);
  const [r11, g11, b11, a11] = px(x0 + 1, y0 + 1);
  const lerp = (p, q, t) => p + (q - p) * t;
  const bl = (p00, p10, p01, p11) =>
    Math.round(lerp(lerp(p00, p10, fx), lerp(p01, p11, fx), fy));
  return [bl(r00, r10, r01, r11), bl(g00, g10, g01, g11),
          bl(b00, b10, b01, b11), bl(a00, a10, a01, a11)];
}

// ─── Pixel Filters ───────────────────────────────────────────────────────────

function applyGrayscale(imageData) {
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    d[i] = d[i + 1] = d[i + 2] = lum;
  }
  return imageData;
}

/** 5×5 Gaussian blur (σ ≈ 1.0). */
function applyGaussianBlur(imageData, width, height) {
  // prettier-ignore
  const kernel = [
    1,  4,  7,  4, 1,
    4, 16, 26, 16, 4,
    7, 26, 41, 26, 7,
    4, 16, 26, 16, 4,
    1,  4,  7,  4, 1,
  ];
  return convolve(imageData, width, height, kernel, 273, 0);
}

function applyContrastBrightness(imageData, factor, delta) {
  const d = imageData.data;
  const intercept = 128 * (1 - factor) + delta;
  for (let i = 0; i < d.length; i += 4) {
    d[i]     = clamp(d[i]     * factor + intercept);
    d[i + 1] = clamp(d[i + 1] * factor + intercept);
    d[i + 2] = clamp(d[i + 2] * factor + intercept);
  }
  return imageData;
}

function applySharpen(imageData, width, height) {
  // prettier-ignore
  const kernel = [
     0, -1,  0,
    -1,  5, -1,
     0, -1,  0,
  ];
  return convolve(imageData, width, height, kernel, 1, 0);
}

function convolve(imageData, width, height, kernel, divisor, bias) {
  const src   = imageData.data;
  const out   = new Uint8ClampedArray(src.length);
  const kSize = Math.round(Math.sqrt(kernel.length));
  const half  = Math.floor(kSize / 2);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let r = 0, g = 0, b = 0;
      for (let ky = 0; ky < kSize; ky++) {
        for (let kx = 0; kx < kSize; kx++) {
          const px  = clamp(x + kx - half, 0, width  - 1);
          const py  = clamp(y + ky - half, 0, height - 1);
          const idx = (py * width + px) * 4;
          const w   = kernel[ky * kSize + kx];
          r += src[idx]     * w;
          g += src[idx + 1] * w;
          b += src[idx + 2] * w;
        }
      }
      const i      = (y * width + x) * 4;
      out[i]     = clamp(r / divisor + bias);
      out[i + 1] = clamp(g / divisor + bias);
      out[i + 2] = clamp(b / divisor + bias);
      out[i + 3] = src[i + 3];
    }
  }
  return new ImageData(out, width, height);
}

// ─── Deskew Fallback ──────────────────────────────────────────────────────────

function deskewCanvas(canvas) {
  const angle = estimateSkewAngle(canvas);
  console.log("Deskew angle:", angle);
  if (Math.abs(angle) < 0.3) return canvas;
  const rad  = (angle * Math.PI) / 180;
  const cos  = Math.abs(Math.cos(rad));
  const sin  = Math.abs(Math.sin(rad));
  const newW = Math.round(canvas.width * cos + canvas.height * sin);
  const newH = Math.round(canvas.height * cos + canvas.width * sin);
  const out  = createCanvas(newW, newH);
  const ctx  = out.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, newW, newH);
  ctx.translate(newW / 2, newH / 2);
  ctx.rotate(rad);
  ctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
  return out;
}

function estimateSkewAngle(canvas) {
  const { width: w, height: h } = canvas;
  const { data } = canvas.getContext("2d").getImageData(0, 0, w, h);
  const step = Math.max(1, Math.floor(w / 300));
  let bestAngle = 0, bestScore = -Infinity;

  for (let a = -15; a <= 15; a += 0.5) {
    const rad     = (a * Math.PI) / 180;
    const rowSums = new Float64Array(h);
    for (let y = 0; y < h; y++) {
      let sum = 0;
      for (let x = 0; x < w; x += step) {
        const cx = x - w / 2, cy = y - h / 2;
        const rx = Math.round(cx * Math.cos(rad) - cy * Math.sin(rad) + w / 2);
        const ry = Math.round(cx * Math.sin(rad) + cy * Math.cos(rad) + h / 2);
        if (rx < 0 || rx >= w || ry < 0 || ry >= h) continue;
        const i = (ry * w + rx) * 4;
        sum += 255 - (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
      }
      rowSums[y] = sum;
    }
    const mean     = rowSums.reduce((a, b) => a + b, 0) / h;
    const variance = rowSums.reduce((a, b) => a + (b - mean) ** 2, 0) / h;
    if (variance > bestScore) { bestScore = variance; bestAngle = a; }
  }
  return bestAngle;
}

// ─── Auto-Crop Fallback ───────────────────────────────────────────────────────

/**
 * Crop away near-white borders to isolate receipt content.
 * Threshold 220 (not 255) to handle off-white receipt paper.
 */
function autoCropCanvas(canvas) {
  const ctx = canvas.getContext("2d");
  const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const THRESHOLD = 220;  // was 245 — receipt paper is typically 220–240
  const PADDING   = 12;
  // const isBg = (i) => data[i] >= THRESHOLD && data[i + 1] >= THRESHOLD && data[i + 2] >= THRESHOLD;
  const isBg = (i) => data[i] < THRESHOLD && data[i + 1] < THRESHOLD && data[i + 2] < THRESHOLD;

  let top = 0, bottom = height - 1, left = 0, right = width - 1;
  console.log("auto-crop start: ", top,left, bottom, right);
  outer: for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) { if (!isBg((y * width + x) * 4)) { top = y; break outer; } }
  }
  outer: for (let y = height - 1; y >= 0; y--) {
    for (let x = 0; x < width; x++) { if (!isBg((y * width + x) * 4)) { bottom = y; break outer; } }
  }
  outer: for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) { if (!isBg((y * width + x) * 4)) { left = x; break outer; } }
  }
  outer: for (let x = width - 1; x >= 0; x--) {
    for (let y = 0; y < height; y++) { if (!isBg((y * width + x) * 4)) { right = x; break outer; } }
  }
  console.log("auto-crop end: ", { top, left, bottom, right });
  top    = Math.max(0,          top    - PADDING);
  left   = Math.max(0,          left   - PADDING);
  bottom = Math.min(height - 1, bottom + PADDING);
  right  = Math.min(width  - 1, right  + PADDING);
  console.log("auto-crop after padding: ", { top, left, bottom, right });
  const cropW = right - left + 1;
  const cropH = bottom - top + 1;
  if (cropW <= 0 || cropH <= 0) return canvas;
  console.log("auto-crop final: ", { cropW, cropH });
  const out = createCanvas(cropW, cropH);
  out.getContext("2d").drawImage(canvas, left, top, cropW, cropH, 0, 0, cropW, cropH);
  return out;
}

// ─── Utilities ────────────────────────────────────────────────────────────────

function clamp(value, min = 0, max = 255) {
  return Math.max(min, Math.min(max, Math.round(value)));
}

// ─── Convenience: process from <input type="file"> ───────────────────────────

/**
 * Wire up a file input element. Calls onResult with the processed output.
 *
 * @param {HTMLInputElement} inputEl
 * @param {function({ dataURL, canvas, stats }): void} onResult
 * @param {object} options
 */
export function bindFileInput(inputEl, onResult, options = {}) {
  inputEl.addEventListener("change", async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await preprocessReceipt(file, options);
      onResult(result);
    } catch (err) {
      console.error("[receiptPreprocessor] Error:", err);
      throw err;
    }
  });
}
